import React, { FocusEvent, InputHTMLAttributes, KeyboardEvent } from 'react';

import PropTypes from 'prop-types';

import theme, {
  stylePropType,
  DetailedStylableComponent,
} from '@adsk/alloy-react-theme';

import { disabledStyle } from '../consts';
import { padNumber } from '../helpers';

const focusStyle = {
  backgroundColor: theme.tokens.colors.background.selected.value,
  color: theme.tokens.colors.text.inverse.value,
  borderRadius: theme.borderRadius.medium,
};

export type TimeInputProps = Omit<
  DetailedStylableComponent<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'ref'
> & {
  value?: string | number;
  disabled?: boolean;
  readOnly?: boolean;
  setFocused?: (id: string | undefined) => void;
  hasTime?: boolean;
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  onArrowRight?: () => void;
  onArrowLeft?: () => void;
  onAm?: () => void;
  onPm?: () => void;
  isTimePeriod?: boolean;
};

const NUMBER_REGEX = /^(\d)$/;
const ALLOWED_KEYS = [
  'Backspace',
  'Delete',
  'ArrowRight',
  'ArrowLeft',
  'Tab',
  'Enter',
];
const TIME_PERIOD_KEYS = ['a', 'A', 'p', 'P'];

const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
  e.currentTarget.value = padNumber(e.currentTarget.value);
};

const preventMenuToggling = (e: React.MouseEvent<HTMLInputElement>) => {
  e.stopPropagation();
};

const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  (
    {
      className,
      style,
      id,
      value,
      disabled,
      readOnly,
      setFocused,
      hasTime,
      onArrowUp,
      onArrowDown,
      onArrowRight,
      onArrowLeft,
      isTimePeriod,
      onAm,
      onPm,
      ...props
    },
    ref,
  ) => {
    const allowedKeys = isTimePeriod
      ? [...ALLOWED_KEYS, ...TIME_PERIOD_KEYS]
      : ALLOWED_KEYS;

    const handleTimePeriodKeys = (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'a':
        case 'A':
          onAm?.();
          break;
        case 'p':
        case 'P':
          onPm?.();
          break;
        default:
          break;
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Enter':
          setFocused?.(undefined);
          e.currentTarget.blur();
          break;
        case 'Escape':
          setFocused?.(undefined);
          e.currentTarget.blur();
          return;
        case 'ArrowUp':
          onArrowUp?.();
          break;
        case 'ArrowDown':
          onArrowDown?.();
          break;
        case 'ArrowRight':
          onArrowRight?.();
          break;
        case 'ArrowLeft':
          onArrowLeft?.();
          break;
        default:
          break;
      }

      if (isTimePeriod) {
        handleTimePeriodKeys(e);
      }

      if (allowedKeys.includes(e.key)) {
        e.stopPropagation();
        return;
      }

      if (NUMBER_REGEX.test(e.key) && +e.currentTarget.value.length < 12) {
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <input
        ref={ref}
        id={id}
        css={[
          {
            color: hasTime
              ? theme.tokens.colors.text.primary.value
              : theme.tokens.colors.text.tertiary.value,
            paddingTop: 2.5,
            paddingBottom: 2.5,
            textAlign: 'center',
            border: 'none',
            outline: 'none',
            width: 24,
            ...(disabled && disabledStyle),
            ':focus': focusStyle,
            '::selection': focusStyle,
            caretColor: 'transparent',
          },
          style,
        ]}
        readOnly={readOnly}
        value={value}
        disabled={disabled}
        onBlur={handleBlur}
        onFocus={(e) => {
          setFocused && setFocused(id);
          e.target.select();
        }}
        onKeyDown={handleKeyDown}
        onClick={preventMenuToggling}
        {...props}
      />
    );
  },
);

TimeInput.displayName = 'TimeInput';

TimeInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle readOnly state */
  readOnly: PropTypes.bool,
  /** Callback when value changes */
  onChange: PropTypes.func,
  /** Element ID */
  id: PropTypes.string,
  /** Callback to set focus */
  setFocused: PropTypes.func,
  /** True if the TimePicker has a time selected  */
  hasTime: PropTypes.bool,
  /** Function to increment input value */
  onArrowUp: PropTypes.func,
  /** Function to decrement input value */
  onArrowDown: PropTypes.func,
  /** Enables behaviour specific to time period display */
  isTimePeriod: PropTypes.bool,
  /** Function to set time period to AM */
  onAm: PropTypes.func,
  /** Function to set time period to PM */
  onPm: PropTypes.func,
};

export default TimeInput;
